import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from '../../../../../../../../components/button/base-button';
import { FullScreenIonModal } from '../../../../../../../../components/modal/full-screen-ion-modal';
import './multipass-guided-view.css';
import { MultipassScheduleIndividualToolbarContent } from '../individual-view/multipass-schedule-individual-view-modal';
import {
  MultipassGuidedView,
  MultipassGuidedViewProps,
} from './multipass-guided-view';
import './multipass-guided-view-modal.css';

export interface MultipassGuidedViewModalProps
  extends Omit<MultipassGuidedViewProps, 'passLabel' | 'changePassIndex'> {
  numberOfPasses: number;
}

export interface MultipassGuidedViewChangeIndexOptions {
  index: number;
  closeIfInvalid?: boolean;
}

export type MultipassGuidedViewChangeIndexFn = (
  options: MultipassGuidedViewChangeIndexOptions
) => void;

const MultipassGuidedViewModal = (props: MultipassGuidedViewModalProps) => {
  const { disabled, presentingEl, isFullScreen, passIndex, numberOfPasses } =
    props;

  const modalRef = useRef<HTMLIonModalElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const { i18n } = useTranslation();

  const [selectedPassIndex, setSelectedPassIndex] = useState<number>(passIndex);

  const passLabel = useMemo(() => {
    return i18n.t('common:pass_with_number', {
      number: selectedPassIndex + 2,
    });
  }, [i18n, selectedPassIndex]);

  const modalTitle = useMemo(() => {
    return `${i18n.t('common:teach')} ${passLabel}`;
  }, [i18n, passLabel]);

  const changePassIndex = useCallback(
    (options: MultipassGuidedViewChangeIndexOptions) => {
      const { index, closeIfInvalid } = options;
      if ((index >= numberOfPasses || index < 0) && closeIfInvalid) {
        modalRef.current?.dismiss();
      } else {
        setSelectedPassIndex(index);
      }
    },
    [numberOfPasses]
  );

  const handleExecutionStateChange = useCallback((executing: boolean) => {
    setIsExecuting(executing);
  }, []);

  return (
    <>
      <BaseButton
        buttonProps={{
          onClick: () => setIsModalOpen(true),
          disabled: disabled,
          fill: 'outline',
          size: 'small',
        }}
      >
        {i18n.t('common:teach')}
      </BaseButton>
      <FullScreenIonModal
        ref={modalRef}
        isOpen={isModalOpen}
        onDidDismiss={() => {
          // reset our pass index to the original. this is because we allow
          // the user to use the toolbar to change the index
          setSelectedPassIndex(passIndex);
          setIsModalOpen(false);
        }}
        presentingElement={presentingEl}
        isFullScreen={isFullScreen}
        className="multipass-guided-view-modal"
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot={'start'}>
              <IonButton
                onClick={() => {
                  modalRef.current?.dismiss();
                }}
              >
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{modalTitle}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <MultipassGuidedView
            {...props}
            passIndex={selectedPassIndex ?? passIndex}
            passLabel={passLabel}
            changePassIndex={changePassIndex}
            onExecutionStateChange={handleExecutionStateChange}
          />
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <MultipassScheduleIndividualToolbarContent
              numberOfPasses={numberOfPasses}
              selectedPassIndex={selectedPassIndex ?? passIndex}
              setSelectedPass={
                setSelectedPassIndex as Dispatch<
                  SetStateAction<number | undefined>
                >
              }
              onDone={() => {
                modalRef.current?.dismiss();
              }}
              isExecuting={isExecuting}
            />
          </IonToolbar>
        </IonFooter>
      </FullScreenIonModal>
    </>
  );
};

export { MultipassGuidedViewModal };
